import { useQuery } from '@tanstack/react-query';
import { PATHS } from 'routes/routes.constants';

import { useRouter } from '@polyai/common/hooks/useRouter';
import { Loader } from '@polyai/ui/components/atoms/Loader';
import Text from '@polyai/ui/components/atoms/Text';

import api from 'api';
import { AssistantType } from 'api/resources/accounts/types';
import useAccounts from 'hooks/useAccounts';
import { useIsFeatureOn } from 'hooks/useFeatureSwitch/useIsFeatureOn';
import { generateNextPath } from 'lib/routeHelper';
import { usePersonalAccount } from 'screens/AgentWizard/hooks/usePersonalAccount';

const KnowledgeBaseShortcut: React.FC = () => {
  const { isFeatureOn, isLoadingFeatures } = useIsFeatureOn();
  const { replaceUrl } = useRouter();

  const { accounts, isLoadingAccounts } = useAccounts();
  const accountId = usePersonalAccount('');

  // Fetch projects for the account
  const {
    data: projectsData,
    isLoading: isLoadingProjects,
    error: projectsError,
  } = useQuery({
    queryKey: [accountId, 'projects'],
    queryFn: () => api.getProjects(accountId!),
    enabled: !!accountId,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Wait for all data to load before computing feature-flag-dependent values
  if (isLoadingAccounts || isLoadingProjects || isLoadingFeatures) {
    return <Loader fullScreen logo />;
  }

  const isPortalProjectsFeatureOn = isFeatureOn('portal_projects_in_jupiter');
  const isMergedKnowledgeEnabled = isFeatureOn('mergedKnowledge');

  // Get the first project (depends on feature flag)
  const firstProject = projectsData?.projects
    ? (() => {
        const filteredProjects = isPortalProjectsFeatureOn
          ? projectsData.projects
          : projectsData.projects.filter(
              project => project.type !== AssistantType.INTENT,
            );
        return filteredProjects[0];
      })()
    : null;

  if (projectsError) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Text variant="body">Failed to load account information</Text>
      </div>
    );
  }

  if (!accounts.length || !accountId) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Text variant="body">No accounts found</Text>
      </div>
    );
  }

  if (!firstProject) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Text variant="body">No projects found</Text>
      </div>
    );
  }

  // Redirect to the appropriate knowledge base path
  const path = isMergedKnowledgeEnabled
    ? generateNextPath(PATHS.KNOWLEDGE, {
        accountId,
        projectId: firstProject.id,
        type: 'managed',
      })
    : generateNextPath(PATHS.KNOWLEDGE_BASE, {
        accountId,
        projectId: firstProject.id,
      });
  replaceUrl(path);

  return <Loader fullScreen logo />;
};

export default KnowledgeBaseShortcut;
